"use client";

import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { captureAdsAttribution } from "@/lib/ads-attribution-client";
import { type ConsentValue } from "@/lib/consent";
import { getConsentChoice, saveConsentChoice, syncMeasurementConsent } from "@/lib/consent-client";
import { useUserId } from "@/lib/nhost/session-hooks";

function updateConsentMode(consent: ConsentValue) {
  if (typeof window === "undefined") return;

  // GTM / Google Consent Mode v2
  if (window.dataLayer) {
    const state = consent === "all" ? "granted" : "denied";
    // gtag's command queue expects an Arguments object, not an ordinary array.
    function gtag(..._args: unknown[]) {
      // biome-ignore lint/complexity/noArguments: required by the gtag command queue protocol
      window.dataLayer?.push(arguments);
    }
    gtag("consent", "update", {
      ad_storage: state,
      ad_user_data: state,
      ad_personalization: state,
      analytics_storage: state,
    });
  }

  if (window.clarity && typeof window.clarity === "function") {
    const state = consent === "all" ? "granted" : "denied";
    window.clarity("consentv2", { ad_Storage: state, analytics_Storage: state });
  }

  // Preserve existing capture behavior; this choice controls SDK persistence.
  posthog.set_config({
    persistence: consent === "all" ? "localStorage+cookie" : "memory",
  });
}

export async function handleConsent(value: ConsentValue) {
  saveConsentChoice(value);
  updateConsentMode(value);
  captureAdsAttribution();
  await syncMeasurementConsent();
}

interface CookieConsentBannerProps {
  message: string;
  withdrawalHint: string;
  acceptButton: string;
  rejectButton: string;
  saveError: string;
  privacyLink: string;
  privacyHref: string;
}

export function CookieConsentBanner({
  message,
  withdrawalHint,
  acceptButton,
  rejectButton,
  saveError,
  privacyLink,
  privacyHref,
}: CookieConsentBannerProps) {
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const userId = useUserId();

  useEffect(() => {
    const value = getConsentChoice();
    setVisible(value !== "all" && value !== "necessary");
    captureAdsAttribution();
    const open = () => setVisible(true);
    window.addEventListener("teksta:cookie-preferences", open);
    return () => window.removeEventListener("teksta:cookie-preferences", open);
  }, []);

  useEffect(() => {
    if (!userId) return;
    syncMeasurementConsent().catch(() => {
      setError(true);
      setVisible(true);
    });
  }, [userId]);

  if (!visible) return null;

  const accept = async (value: ConsentValue) => {
    setSaving(true);
    setError(false);
    try {
      await handleConsent(value);
      setVisible(false);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-60 rounded-xl border bg-background p-4 shadow-lg sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-lg">
      <div className="flex flex-col gap-3">
        <div className="min-w-0 text-muted-foreground">
          <p className="text-sm">{message}</p>
          <p className="mt-2 text-xs">{withdrawalHint}</p>
        </div>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {saveError}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <a
            href={privacyHref}
            target="_blank"
            rel="noopener noreferrer"
            className="mr-auto text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {privacyLink}
          </a>
          <Button variant="outline" size="sm" disabled={saving} onClick={() => accept("necessary")}>
            {rejectButton}
          </Button>
          <Button size="sm" disabled={saving} onClick={() => accept("all")}>
            {acceptButton}
          </Button>
        </div>
      </div>
    </div>
  );
}
